import { cartAtom } from "@/shared/atoms";
import { getBigAndSmallCartItemByContent } from "@/shared/utils/helpers";
import { CartItem } from "@/shared/utils/interfaces";
import { useAtom, useSetAtom } from "jotai";
import { useSetPosOrderPayment } from "../atoms/usePosOrderPaymentAtom";
import { useSetPosShowPayment } from "../atoms/usePosShowPayment";
import usePosMenuGetPriceHook from "./usePosMenuGetPriceHook";
import usePosMenuGetVatHook from "./usePosMenuGetVatHook";
import { useHasSession, useShowPayments } from "./usePosOrderTableSessionHook";
import { PaymentMethodEnum } from "../utils/enum";
import { useSetPosOrderSelectedCartItemIndex } from "../atoms/usePosOrderSelectedCartItemIndex";

export const useCart = () => useAtom(cartAtom);

export const useSetCart = () => useSetAtom(cartAtom);

export const useAddCartItem = () => {
  const setCart = useSetCart();
  const resetPaymentSession = useResetPaymentSession();
  const setSelectedCartItemIndex = useSetPosOrderSelectedCartItemIndex();
  const getPrice = usePosMenuGetPriceHook();
  const getVat = usePosMenuGetVatHook();

  return (cartItem: CartItem) => {
    resetPaymentSession();
    const _cartItem = {
      ...cartItem,
      price: getPrice(cartItem),
      vat_percentage: getVat(cartItem),
    };

    setCart((currentCart) => {
      if (!isAlreadyInCart(currentCart, _cartItem)) {
        setSelectedCartItemIndex(currentCart.length);
        return [...currentCart, _cartItem];
      }
      return currentCart.map((currentItem, index) => {
        const isMatched = match(currentItem, _cartItem);
        isMatched && setSelectedCartItemIndex(index);
        return isMatched
          ? {
              ...currentItem,
              quantity: currentItem.quantity + _cartItem.quantity,
              selected_sub_content: currentItem.selected_sub_content?.map(
                (sub) => ({
                  ...sub,
                  quantity: sub.quantity + _cartItem.quantity,
                }),
              ),
            }
          : currentItem;
      });
    });
  };
};

export const useDecreaseCartItem = () => {
  const setCart = useSetCart();
  const resetPaymentSession = useResetPaymentSession();

  return (cartItem: CartItem) => {
    resetPaymentSession();
    setCart((currentCart) => {
      if (isAlreadyInCart(currentCart, cartItem)) {
        return currentCart
          .map((currentItem) => {
            return match(currentItem, cartItem)
              ? {
                  ...currentItem,
                  quantity: currentItem.quantity - 1,
                  selected_sub_content: currentItem.selected_sub_content?.map(
                    (sub) => ({
                      ...sub,
                      quantity: sub.quantity - 1,
                    }),
                  ),
                }
              : currentItem;
          })
          ?.filter((currentItem) => currentItem.quantity > 0);
      }
      return [];
    });
  };
};

export const useResetCart = () => {
  const setCart = useSetCart();
  return () => setCart([]);
};

export const useRemoveCartItem = () => {
  const setCart = useSetCart();
  const resetPaymentSession = useResetPaymentSession();
  return (index: number) => {
    resetPaymentSession();
    setCart((currentCart) => currentCart.filter((_, i) => i !== index));
  };
};

export const useUpdateCartItemById = () => {
  const setCart = useSetCart();
  const resetPaymentSession = useResetPaymentSession();

  return (itemId: number, item: CartItem) => {
    resetPaymentSession();
    setCart((cart) =>
      cart.map((current) => (itemId === current.item_id ? item : current)),
    );
  };
};

export const useUpdateCartItemByIndex = () => {
  const setCart = useSetCart();

  const resetPaymentSession = useResetPaymentSession();
  return (index: number, item: CartItem) => {
    resetPaymentSession();
    setCart((cart) => cart.map((current, i) => (i === index ? item : current)));
  };
};

export const useReplaceCartItem = () => {
  const setCart = useSetCart();

  return (cartItem: CartItem, index: number) =>
    setCart((cart) =>
      cart.map((current, i) => (i === index ? cartItem : current)),
    );
};

export const useCartTotal = () => {
  const [cart] = useCart();
  const getCartItemTotal = useCartItemTotal();
  return cart.reduce((acc, cur) => acc + getCartItemTotal(cur), 0);
};

export const useCartItemTotal = () => {
  return (cartItem: CartItem, field: string = "selected_sub_content") => {
    const basePrice = parseFloat(cartItem.price ?? "0");
    const toppingPrice = cartItem?.[field]?.reduce(
      (prev: number, current: CartItem) =>
        prev + parseFloat(current.price ?? "0"),
      0,
    );
    return (basePrice + (toppingPrice ?? 0)) * cartItem.quantity;
  };
};

export const useCartQuantity = () => {
  const [cart] = useCart();
  return cart.reduce((acc, cur) => acc + cur.quantity, 0);
};

export const useIsCartEmpty = () => {
  const [cart] = useCart();
  return cart.length === 0;
};

/* -----functions----- */

const match = (currentItem: CartItem, nextItem: CartItem) => {
  const { small, big } = getBigAndSmallCartItemByContent([
    currentItem,
    nextItem,
  ]);
  const isMatch =
    currentItem.item_id === nextItem.item_id &&
    currentItem.parent_id === nextItem.parent_id &&
    currentItem.note === nextItem.note
      ? big?.selected_sub_content === undefined &&
        small?.selected_sub_content === undefined
        ? true
        : big?.selected_sub_content !== undefined &&
            small?.selected_sub_content !== undefined
          ? big?.selected_sub_content?.every((currentSubContent) =>
              small?.selected_sub_content?.find(
                (nSb) =>
                  nSb.item_id === currentSubContent.item_id &&
                  nSb.parent_id === currentSubContent.parent_id,
              ),
            )
          : false
      : false;
  return isMatch;
};

const isAlreadyInCart = (cart: CartItem[], cartItem: CartItem) =>
  cart?.find((currentItem) => match(currentItem, cartItem)) !== undefined;

const useResetPaymentSession = () => {
  const hasSession = useHasSession();
  const showPayments = useShowPayments();
  const setPayment = useSetPosOrderPayment();
  const setPosShowPayment = useSetPosShowPayment();

  return () => {
    if (hasSession && showPayments) {
      setPayment(PaymentMethodEnum.PAYMENT_METHOD_NOT_KNOWN_YET);
      setPosShowPayment(false);
    }
  };
};
